import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowRight, Check, X } from "lucide-react";

export const Route = createFileRoute("/why-texas-made-shade")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Why Texas Made Shade vs. Typical Installers | Texas Made Shade",
      description:
        "Custom fabrication, in-house welding, in-house sewing, Solair® dealer, 30 years experience. See why Texas Made Shade beats typical shade installers.",
      keywords:
        "why Texas Made Shade, custom awning company Texas, awning fabrication vs installer, Solair® dealer Texas, in-house welding awning shop, San Antonio awning company, Austin awning company",
    }),
    scripts: serviceHeadScripts({
      path: "/why-texas-made-shade",
      serviceName: "Why Texas Made Shade",
      breadcrumbName: "Why Us",
      description: "Why choose Texas Made Shade over typical shade installers — in-house fabrication, welding, sewing, Solair® dealer and 30 years of Texas experience.",
    }),
  }),
  component: WhyPage,
});

const ROWS: { feature: string; us: boolean | string; them: boolean | string }[] = [
  { feature: "Custom fabrication (not just installs)", us: true, them: false },
  { feature: "In-house welding (aluminum & steel)", us: true, them: "Rare" },
  { feature: "In-house sewing & canvas patterning", us: true, them: "Rare" },
  { feature: "Authorized Solair® dealer", us: true, them: "Varies" },
  { feature: "Commercial engineering & stamped plans", us: true, them: "Limited" },
  { feature: "Residential projects", us: true, them: true },
  { feature: "Recover, repair & service work", us: true, them: "Often limited" },
  { feature: "30+ years in business", us: true, them: "Varies" },
  { feature: "Real Texas shop you can visit", us: "Seguin, TX", them: "Usually no" },
  { feature: "Sunbrella®, Tempotest®, Phifertex®, Somfy®", us: true, them: "Some" },
  { feature: "Local crews — not subcontracted", us: true, them: "Often subbed" },
  { feature: "Direct line to the owner", us: true, them: "1-800 number" },
];

function cell(v: boolean | string, ok = true) {
  if (v === true) return <span className="inline-flex items-center gap-2 font-semibold"><Check className="h-4 w-4 text-red" /> Yes</span>;
  if (v === false) return <span className="inline-flex items-center gap-2 text-navy-deep/60"><X className="h-4 w-4 text-navy-deep/70" /> No</span>;
  return <span className={`inline-flex items-center gap-2 ${ok ? "font-semibold" : "text-navy-deep/70"}`}>{ok ? <Check className="h-4 w-4 text-red" /> : <X className="h-4 w-4 text-navy-deep/70" />} {v}</span>;
}

function WhyPage() {
  return (
    <div className="min-h-screen bg-cream text-navy-deep">
      <SiteHeader />
      <section className="bg-navy-deep py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Breadcrumbs items={[{ label: "Why Texas Made Shade" }]} className="text-white/80" />
          <div className="mt-6 font-display text-xs uppercase tracking-[0.3em] text-red">★ WHY US ★</div>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-bold uppercase leading-tight md:text-5xl">
            Why Texas Made Shade — Not Just Another Installer
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/85">
            Most shade companies you find online are marketing-and-installation shops that resell catalog products. Texas Made Shade is a 30-year Texas fabrication shop. Here's the side-by-side.
          </p>
        </div>
      </section>

      <section className="bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-navy-deep/15 bg-white text-sm">
              <thead className="bg-navy-deep text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-display uppercase tracking-wide">Feature</th>
                  <th className="px-4 py-3 text-left font-display uppercase tracking-wide">Texas Made Shade</th>
                  <th className="px-4 py-3 text-left font-display uppercase tracking-wide">Typical Installer</th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r.feature} className={i % 2 ? "bg-cream/50" : ""}>
                    <td className="border-t border-navy-deep/10 px-4 py-3 font-semibold">{r.feature}</td>
                    <td className="border-t border-navy-deep/10 px-4 py-3">{cell(r.us, true)}</td>
                    <td className="border-t border-navy-deep/10 px-4 py-3">{cell(r.them, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Link to="/built-in-texas" className="group flex h-full flex-col border-l-4 border-red bg-white p-5 transition hover:bg-cream">
              <span className="font-display text-base font-bold uppercase text-navy-deep group-hover:text-red">Built In Texas</span>
              <span className="mt-1 text-sm text-navy-deep/75">See our Seguin fabrication shop and capabilities.</span>
              <span className="mt-3 inline-flex items-center gap-1 font-display text-[11px] uppercase tracking-[0.25em] text-red">Explore <ArrowRight className="h-3 w-3" /></span>
            </Link>
            <Link to="/retractable-awnings" className="group flex h-full flex-col border-l-4 border-red bg-white p-5 transition hover:bg-cream">
              <span className="font-display text-base font-bold uppercase text-navy-deep group-hover:text-red">Authorized Solair® Dealer</span>
              <span className="mt-1 text-sm text-navy-deep/75">Premium retractable awnings — Sunbrella, Somfy, full hood.</span>
              <span className="mt-3 inline-flex items-center gap-1 font-display text-[11px] uppercase tracking-[0.25em] text-red">View Solair® <ArrowRight className="h-3 w-3" /></span>
            </Link>
            <Link to="/custom-fabrication" className="group flex h-full flex-col border-l-4 border-red bg-white p-5 transition hover:bg-cream">
              <span className="font-display text-base font-bold uppercase text-navy-deep group-hover:text-red">Custom Fabrication</span>
              <span className="mt-1 text-sm text-navy-deep/75">Welding, steel, aluminum, one-off projects.</span>
              <span className="mt-3 inline-flex items-center gap-1 font-display text-[11px] uppercase tracking-[0.25em] text-red">See work <ArrowRight className="h-3 w-3" /></span>
            </Link>
          </div>
        </div>
      </section>

      <QuoteCTA title="Ready For A Real Texas Shade Shop?" subtitle="Get a free quote from the fabricator who actually builds your shade." />
      <SiteFooter />
    </div>
  );
}
